import { Component } from 'react';
import { Cell } from '@table-library/react-table-library/table';
import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import Loading from '../../../../components/Loading';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { reportsPosition } from '../../GscTools/metrics';
import { formatPeriod } from '../../GscTools/period';
import { segmentColumns } from '../keys';
import { SEGMENT, segmentKeyLabel, segmentLabel } from '../segments';

/**
 * Block 01 — the property's traffic cut by the selected dimension, this window against its
 * baseline.
 *
 * The rows are a partition of one property's traffic, not a population: a country row counts the
 * impressions Google served in that country, which is not the same as the people who live there,
 * and nothing in the response says anything about who they are. Appearance rows are not even a
 * partition — Search Console counts one impression under every appearance that applied to it, so
 * those rows overlap and must never be summed into a property total. The note under the table
 * says so, under whichever dimension is in force.
 *
 * A key returned in only one of the two windows has no change: it is shown as not comparable
 * rather than as a gain from zero, because Search Console withholding a row is not the same as
 * the row being absent.
 */
class SegmentTable extends Component {
    constructor(props) {
        super(props);
        this.searchFunction = this.searchFunction.bind(this);
        this.actionColumn = this.actionColumn.bind(this);
        this.handleRowClick = this.handleRowClick.bind(this);
    }

    /**
     * Clicking the row does what its button does, including toggling back off.
     *
     * The same handler rather than a second path to the same place, so the two can never drift:
     * whatever the button means by "open this segment", the row means too.
     */
    handleRowClick(item) {
        const { selectedKey, onSelect } = this.props;
        onSelect(item.key === selectedKey ? null : item.key);
    }

    /** Searching reads both what the row is called and the key it was returned under. */
    searchFunction(data, search) {
        const { t, scope } = this.props;
        if (!search) return data;

        const term = search.toLowerCase();
        return data.filter(
            (item) =>
                item.key?.toLowerCase().includes(term) ||
                segmentKeyLabel(t, scope.segment, item.key).toLowerCase().includes(term),
        );
    }

    /** The button that loads one segment's daily history into the block below. */
    actionColumn() {
        const { t, selectedKey, onSelect } = this.props;

        return {
            label: t('tools.audience_appearance.segment_table.trend_column'),
            key: 'trend',
            data: 'key',
            sortType: (array) => array,
            cellSize: 'minmax(130px, 150px)',
            Component: Cell,
            renderCell: ({ item, csvDownload }) => {
                if (csvDownload) return '';

                const picked = item.key === selectedKey;

                return (
                    <div className="d-flex">
                        <button
                            type="button"
                            className={`table-action-main-btn ${picked ? 'is-picked' : ''}`}
                            aria-pressed={picked}
                            onClick={() => onSelect(picked ? null : item.key)}
                        >
                            {picked && <i className="icon-check-icon" aria-hidden="true" />}
                            {picked
                                ? t('tools.audience_appearance.segment_table.trend_showing')
                                : t('tools.audience_appearance.segment_table.trend_action')}
                        </button>
                    </div>
                );
            },
        };
    }

    render() {
        const { t, scope, status, error, rows, returnedRows, selectedKey, onExport } = this.props;

        const columns = segmentColumns(t, {
            segment: scope.segment,
            hasPosition: reportsPosition(scope.searchType),
        });
        const tableColumns = [...columns, this.actionColumn()];

        return (
            <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                <div className="card-header d-flex justify-content-between">
                    <div>
                        <p className="card-title">
                            {t('tools.audience_appearance.segment_table.title', {
                                segment: segmentLabel(t, scope.segment),
                            })}
                        </p>
                        <p className="gsc-tool-sub">
                            {t('tools.audience_appearance.segment_table.description')} ·{' '}
                            {formatPeriod(scope.current)}
                        </p>
                        <p className="gsc-tool-rows-hint">
                            <i className="icon-info" aria-hidden="true" />
                            {t('tools.audience_appearance.segment_table.select_hint')}
                        </p>
                    </div>

                    <div className="gsc-tool-icon-actions">
                        <button
                            type="button"
                            title={t('tools.audience_appearance.coverage.export')}
                            aria-label={t('tools.audience_appearance.coverage.export')}
                            onClick={onExport}
                            disabled={status !== 'done'}
                        >
                            <i className="icon-fi_download" />
                        </button>
                    </div>
                </div>

                <div className="card-body">
                    {(status === 'idle' || status === 'loading') && <Loading padding={20} />}

                    {status === 'error' && (
                        <div className="gsc-tool-banner error">
                            {error ?? t('global.something_went_wrong')}
                        </div>
                    )}

                    {status === 'done' && (
                        <>
                            {scope.droppedFilters && (
                                <div className="gsc-tool-banner">
                                    {t('tools.audience_appearance.warnings.appearance_alone')}
                                </div>
                            )}

                            <DataTable
                                nodes={rows}
                                COLUMNS={tableColumns}
                                customColumnsForExport={columns}
                                sortFns={getSortKeys(tableColumns)}
                                additionalStyles={setAdditionalStyles(tableColumns)}
                                searchFunction={this.searchFunction}
                                fileName={t('tools.audience_appearance.segment_table.file', {
                                    segment: segmentLabel(t, scope.segment),
                                })}
                                customEmptyMessage={t(
                                    'tools.audience_appearance.segment_table.empty',
                                )}
                                customContainerStyles="gsc-tool-key-table"
                                rowClassName={(item) =>
                                    item.key === selectedKey ? 'dt-row-picked' : ''
                                }
                                onRowClick={this.handleRowClick}
                            />

                            <p className="gsc-tool-table-note">
                                {t('tools.audience_appearance.segment_table.returned', {
                                    rows: returnedRows,
                                })}
                            </p>
                            <p className="gsc-tool-table-note">
                                {scope.segment === SEGMENT.APPEARANCE
                                    ? t('tools.audience_appearance.segment_table.overlap_note')
                                    : t('tools.audience_appearance.segment_table.not_people')}
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default withTranslation()(SegmentTable);
